import { useEffect, useState } from 'react';
import { Cookie, SlidersHorizontal, X } from 'lucide-react';
import { DEFAULT_CONSENT, loadConsent, saveConsent } from '../consent';

export function openCookiePreferences() {
  window.dispatchEvent(new Event('adpulse:open-consent'));
}

function ConsentManager({ onConsentSaved, onOpenLegal }) {
  const [consent, setConsent] = useState(() => loadConsent());
  const [customizing, setCustomizing] = useState(false);
  const [draft, setDraft] = useState(() => loadConsent() || DEFAULT_CONSENT);

  useEffect(() => {
    const open = () => {
      setDraft(loadConsent() || DEFAULT_CONSENT);
      setCustomizing(true);
    };
    window.addEventListener('adpulse:open-consent', open);
    return () => window.removeEventListener('adpulse:open-consent', open);
  }, []);

  const persist = (next, action) => {
    const saved = saveConsent(next, action);
    setConsent(saved);
    setDraft(saved);
    setCustomizing(false);
    onConsentSaved?.(saved);
  };

  const reject = () => persist(DEFAULT_CONSENT, 'reject_nonessential');
  const acceptAll = () => persist(
    { ...DEFAULT_CONSENT, functional: true, analytics: true, marketing: true },
    'accept_all',
  );

  return (
    <>
      {!consent && !customizing && (
        <aside className="consent-banner" role="dialog" aria-modal="false" aria-labelledby="consent-title">
          <div className="consent-banner-copy">
            <span className="consent-icon" aria-hidden="true"><Cookie size={20} /></span>
            <div>
              <h2 id="consent-title">Your privacy choices</h2>
              <p>
                Essential storage keeps sign-in and security working. Optional preferences and Google-hosted
                typography stay off until you choose them. No analytics or marketing tracker is currently installed.
                {' '}<button type="button" className="text-link" onClick={() => onOpenLegal?.('cookies')}>Cookie Policy</button>
              </p>
            </div>
          </div>
          <div className="consent-actions">
            <button type="button" className="secondary-button" onClick={reject}>Reject non-essential</button>
            <button type="button" className="secondary-button" onClick={() => setCustomizing(true)}>
              <SlidersHorizontal size={15} /> Customize
            </button>
            <button type="button" className="primary-button" onClick={acceptAll}>Accept all</button>
          </div>
        </aside>
      )}

      {customizing && (
        <div className="settings-overlay consent-preferences-overlay" onMouseDown={(event) => {
          if (event.target === event.currentTarget && consent) setCustomizing(false);
        }}>
          <section className="consent-preferences panel glow-card" role="dialog" aria-modal="true" aria-labelledby="cookie-preferences-title">
            {consent && (
              <button type="button" className="settings-close" onClick={() => setCustomizing(false)} aria-label="Close cookie preferences">
                <X size={18} />
              </button>
            )}
            <p className="eyebrow">Privacy controls</p>
            <h2 id="cookie-preferences-title">Cookie and storage preferences</h2>
            <p className="consent-intro">You can change these choices at any time. Rejecting optional categories does not block core website access.</p>

            <div className="consent-category-list">
              <label className="consent-category is-required">
                <span><strong>Strictly necessary</strong><small>Supabase session, security, and this consent record.</small></span>
                <input type="checkbox" checked disabled aria-label="Strictly necessary storage is always active" />
              </label>
              <label className="consent-category">
                <span><strong>Functional preferences</strong><small>Visitor theme/language, local tutorial progress, and Google-hosted interface/editor fonts.</small></span>
                <input
                  type="checkbox"
                  checked={Boolean(draft.functional)}
                  onChange={(event) => setDraft((current) => ({ ...current, functional: event.target.checked }))}
                />
              </label>
              <label className="consent-category">
                <span><strong>Analytics</strong><small>No analytics tracker is currently installed. This choice reserves your preference for any future integration.</small></span>
                <input
                  type="checkbox"
                  checked={Boolean(draft.analytics)}
                  onChange={(event) => setDraft((current) => ({ ...current, analytics: event.target.checked }))}
                />
              </label>
              <label className="consent-category">
                <span><strong>Marketing</strong><small>No advertising or retargeting tracker is currently installed.</small></span>
                <input
                  type="checkbox"
                  checked={Boolean(draft.marketing)}
                  onChange={(event) => setDraft((current) => ({ ...current, marketing: event.target.checked }))}
                />
              </label>
            </div>

            <div className="consent-preferences-footer">
              <button type="button" className="text-link" onClick={() => onOpenLegal?.('cookies')}>Read Cookie Policy</button>
              <div className="consent-actions">
                <button type="button" className="secondary-button" onClick={reject}>Reject non-essential</button>
                <button type="button" className="primary-button" onClick={() => persist(draft, 'save_preferences')}>Save preferences</button>
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export default ConsentManager;
